import { FONT_PAIRINGS, deriveTokens, type Theme } from '@content-scheduler/shared/render';
import type { CSSProperties } from 'react';

const RADIUS = {
    square: { container: '0px', bar: '0px', control: '0px', chip: '2px' },
    rounded: { container: '16px', bar: '12px', control: '10px', chip: '999px' },
    pill: { container: '28px', bar: '24px', control: '999px', chip: '999px' },
} as const;

const fontStack = (face: { family: string; fallback: string }) =>
    `'${face.family}', ${face.fallback === 'serif' ? 'Georgia, serif' : 'system-ui, sans-serif'}`;

/** The theme as CSS custom properties, set on the template root. */
export function themeStyle(theme: Theme): CSSProperties {
    const t = deriveTokens(theme);
    const radius = RADIUS[theme.corners];
    const fonts = FONT_PAIRINGS[theme.fontPairing];
    return {
        '--cs-bg': t.bg,
        '--cs-fg': t.fg,
        '--cs-muted': t.muted,
        '--cs-border': t.border,
        '--cs-primary': t.primary,
        '--cs-on-primary': t.onPrimary,
        '--cs-primary-text': t.primaryText,
        '--cs-accent': t.accent,
        '--cs-on-accent': t.onAccent,
        '--cs-tint': t.tint,
        '--cs-on-tint': t.onTint,
        '--cs-radius': radius.container,
        '--cs-radius-bar': radius.bar,
        '--cs-radius-control': radius.control,
        '--cs-radius-chip': radius.chip,
        '--cs-font-heading': fontStack(fonts.heading),
        '--cs-font-body': fontStack(fonts.body),
        '--cs-heading-weight': String(fonts.heading.weights[0]),
    } as CSSProperties;
}

/** One Google Fonts request for both families of a pairing, with only the weights it uses. */
export function fontStylesheetUrl(pairing: Theme['fontPairing']): string {
    const { heading, body } = FONT_PAIRINGS[pairing];
    const weights = new Map<string, Set<number>>();
    for (const face of [heading, body]) {
        const set = weights.get(face.family) ?? new Set<number>();
        face.weights.forEach((w) => set.add(w));
        weights.set(face.family, set);
    }
    const families = [...weights].map(
        ([family, set]) =>
            `family=${family.replace(/ /g, '+')}:wght@${[...set].sort((a, b) => a - b).join(';')}`,
    );
    return `https://fonts.googleapis.com/css2?${families.join('&')}&display=swap`;
}

export function loadFontPairing(pairing: Theme['fontPairing'], doc: Document = document) {
    const href = fontStylesheetUrl(pairing);
    if (doc.head.querySelector(`link[href="${CSS.escape(href)}"]`)) return;
    const link = doc.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    doc.head.appendChild(link);
}
